import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, vShow as _vShow, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "flex-container justify-content-ceter m-2" }
const _hoisted_3 = { class: "text-danger h2" }

import { ref, onMounted, watch } from 'vue';
import IExpiryDatesScan from './components/IExpiryDatesScan';
import ExpiryTableSection from '@/components/ExpiryTableSection.vue';
import { formatDate } from '@/Filters/dateFilter';

// Props
interface Props {
  securityOfficerId?: string;
  baseUrl?: string;
}

const LabelsPerPage = 10;
const smsapi_endpoint_base_url = '';

// Reactive state

export default /*@__PURE__*/_defineComponent({
  __name: 'ExpiryDatesScanView',
  props: {
    securityOfficerId: {},
    baseUrl: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

// Constants
const visaExpiryList = ref<IExpiryDatesScan[]>([]);
const passportExpiryList = ref<IExpiryDatesScan[]>([]);
const siaBadgeExpiryList = ref<IExpiryDatesScan[]>([]);
const searchString = ref<string>('');
const currentVisaExpiryPageNo = ref<number>(0);
const currentPassportExpiryPageNo = ref<number>(0);
const currentSiaBadgeExpiryPageNo = ref<number>(0);
const recordsPerPage = ref<number>(10);
const isAuthorized = ref<boolean>(false);

// Visa pagination state
const visaStartPageNumber = ref<number>(1);
const visaEndPageNumber = ref<number>(0);
const visaIsRangeGreater = ref<boolean>(false);
const visaIsRangeLess = ref<boolean>(false);
const visaExpiryCount = ref<number>(0);

// Passport pagination state
const passportStartPageNumber = ref<number>(1);
const passportEndPageNumber = ref<number>(0);
const passportIsRangeGreater = ref<boolean>(false);
const passportIsRangeLess = ref<boolean>(false);
const passportExpiryCount = ref<number>(0);

// SIA Badge pagination state
const siaStartPageNumber = ref<number>(1);
const siaEndPageNumber = ref<number>(0);
const siaIsRangeGreater = ref<boolean>(false);
const siaIsRangeLess = ref<boolean>(false);
const siaBadgeExpiryCount = ref<number>(0);

// Summary counts
const visaExpiredCount = ref<number>(0);
const visaImmediatelyExpiringCount = ref<number>(0);
const visaSoonExpiringCount = ref<number>(0);
const visaValidCount = ref<number>(0);

const passportExpiredCount = ref<number>(0);
const passportImmediatelyExpiringCount = ref<number>(0);
const passportSoonExpiringCount = ref<number>(0);
const passportValidCount = ref<number>(0);

const siaExpiredCount = ref<number>(0);
const siaImmediatelyExpiringCount = ref<number>(0);
const siaSoonExpiringCount = ref<number>(0);
const siaValidCount = ref<number>(0);

// Filter state
const currentFilters = ref<{ [key: string]: any }>({});

// Handle filter change from child component
const handleFilterChange = (documentType: string, filters: any[]) => {
  // Calculate MinDaysUntilExpiry and MaxDaysUntilExpiry from checked filters
  let minDays: number | null = null;
  let maxDays: number | null = null;

  filters.forEach((item: any) => {
    if (!item.checked) return;

    let itemMin: number | null = null;
    let itemMax: number | null = null;

    switch (item.key) {
      case 'expired':
        itemMin = undefined;//-999;
        itemMax = 0;
        break;
      case 'immediately':
        itemMin = 1;
        itemMax = 7;
        break;
      case 'soon':
        itemMin = 8;
        itemMax = 29;
        break;
      case 'valid':
        itemMin = 30;
        itemMax = undefined;//999;
        break;
    }

    if (itemMin !== null) {
      if (minDays === null || itemMin < minDays) minDays = itemMin;
    }
    if (itemMax !== null) {
      if (maxDays === null || itemMax > maxDays) maxDays = itemMax;
    }
  });

   const anyChecked = filters.some((item: any) => item.checked);
  if (!anyChecked) {
    // Use a range that returns no records
    minDays = 999;
    maxDays = -999;
  } 

  // Store current filters
  currentFilters.value[documentType] = { minDays, maxDays };

  //console.log("minDays, maxDays = ", minDays, maxDays);

  // Reset to page 0 and fetch with new filters - USE THE WORKING FUNCTIONS
  switch (documentType) {
    case 'Visa':
      currentVisaExpiryPageNo.value = 0;
      visaExpiryDatesDisplayNew(0, false);  // ← CHANGE THIS
      break;
    case 'Passport':
      currentPassportExpiryPageNo.value = 0;
      passportExpiryDatesDisplayNew(0);     // ← CHANGE THIS
      break;
    case 'SIA_Badge':
      currentSiaBadgeExpiryPageNo.value = 0;
      siaBadgeExpiryDatesDisplayNew(0);     // ← CHANGE THIS
      break;
  }
};

// Generic function to fetch expiry dates with filters
const fetchExpiryDates = (documentType: string, pageNumber: number, minDays?: number | null, maxDays?: number | null): Promise<any> => {
  const body: any = {
    'PageNumber': pageNumber,
    'RecordsPerPage': recordsPerPage.value,
    'SearchString': searchString.value,
  };

  // Add days filters if provided
  if (minDays !== undefined && minDays !== null) {
    body['MinDaysUntilExpiry'] = minDays;
  }
  if (maxDays !== undefined && maxDays !== null) {
    body['MaxDaysUntilExpiry'] = maxDays;
  }

  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body),
  };

  const url = (props.baseUrl ?? smsapi_endpoint_base_url) + 
    `Securityofficers/GetExpiryDates?documentType=${documentType}`;

  return fetch(url, options)
    .then((response: Response) => {
      if (response.status === 401) {
        isAuthorized.value = false;
        return null;
      }
      isAuthorized.value = true;
      return response.json();
    });
};

const visaExpiryDatesDisplayNew = (pageNumber: number, reset: boolean = false): void => {
  currentVisaExpiryPageNo.value = pageNumber;

  const filters = currentFilters.value['Visa'] || {};
  fetchExpiryDates('Visa', pageNumber, filters.minDays, filters.maxDays)
    .then(results => {
      if (!results) return;
      
      visaExpiryList.value = results.expiriesViewList;
      visaExpiryCount.value = results.totalRecordCount;
      
      // Update summary counts
      visaExpiredCount.value = results.expiredCount || 0;
      visaImmediatelyExpiringCount.value = results.immediatelyExpiringCount || 0;
      visaSoonExpiringCount.value = results.soonExpiringCount || 0;
      visaValidCount.value = results.validCount || 0;

      const visaResultPages = Math.ceil(visaExpiryCount.value / recordsPerPage.value);
      displayVisaResultPageList(currentVisaExpiryPageNo.value, visaResultPages);

      if (pageNumber === 0 && reset === true) {
        currentPassportExpiryPageNo.value = 0;
        passportExpiryDatesDisplayNew(0);
        currentSiaBadgeExpiryPageNo.value = 0;
        siaBadgeExpiryDatesDisplayNew(0);
      }
    })
    .catch((error: Error) => {
      console.error("Error in visaExpiryDatesDisplayNew:", error);
    });
};

const passportExpiryDatesDisplayNew = (pageNumber: number): void => {
  currentPassportExpiryPageNo.value = pageNumber;

  const filters = currentFilters.value['Passport'] || {};
  fetchExpiryDates('Passport', pageNumber, filters.minDays, filters.maxDays)
    .then(results => {
      if (!results) return;
      
      passportExpiryList.value = results.expiriesViewList;
      passportExpiryCount.value = results.totalRecordCount;
      
      passportExpiredCount.value = results.expiredCount || 0;
      passportImmediatelyExpiringCount.value = results.immediatelyExpiringCount || 0;
      passportSoonExpiringCount.value = results.soonExpiringCount || 0;
      passportValidCount.value = results.validCount || 0;

      const passportResultPages = Math.ceil(passportExpiryCount.value / recordsPerPage.value);
      displayPassportResultPageList(currentPassportExpiryPageNo.value, passportResultPages);
    })
    .catch((error: Error) => {
      console.error("Error in passportExpiryDatesDisplayNew:", error);
    });
};

const siaBadgeExpiryDatesDisplayNew = (pageNumber: number): void => {
  currentSiaBadgeExpiryPageNo.value = pageNumber;

  const filters = currentFilters.value['SIA_Badge'] || {};
  fetchExpiryDates('SIA_Badge', pageNumber, filters.minDays, filters.maxDays)
    .then(results => {
      if (!results) return;
      
      siaBadgeExpiryList.value = results.expiriesViewList;
      siaBadgeExpiryCount.value = results.totalRecordCount;
      
      siaExpiredCount.value = results.expiredCount || 0;
      siaImmediatelyExpiringCount.value = results.immediatelyExpiringCount || 0;
      siaSoonExpiringCount.value = results.soonExpiringCount || 0;
      siaValidCount.value = results.validCount || 0;

      const siaResultPages = Math.ceil(siaBadgeExpiryCount.value / recordsPerPage.value);
      displaySiaResultPageList(currentSiaBadgeExpiryPageNo.value, siaResultPages);
    })
    .catch((error: Error) => {
      console.error("Error in siaBadgeExpiryDatesDisplayNew:", error);
    });
};

const displayVisaResultPageList = (currentPageNo, totalResultPages: number = 4): void => {
  const sectionNo = Math.floor(currentPageNo / LabelsPerPage);
  visaStartPageNumber.value = sectionNo * LabelsPerPage;
  visaEndPageNumber.value = (sectionNo + 1) * LabelsPerPage;

  if (visaEndPageNumber.value >= totalResultPages) {
    visaIsRangeGreater.value = false;
    visaEndPageNumber.value = totalResultPages;
  } else {
    visaIsRangeGreater.value = true;
  }

  visaIsRangeLess.value = visaStartPageNumber.value >= LabelsPerPage;
};

const displayPassportResultPageList = (currentPageNo, totalResultPages: number = 4): void => {
  const sectionNo = Math.floor(currentPageNo / LabelsPerPage);
  passportStartPageNumber.value = sectionNo * LabelsPerPage;
  passportEndPageNumber.value = (sectionNo + 1) * LabelsPerPage;

  if (passportEndPageNumber.value >= totalResultPages) {
    passportIsRangeGreater.value = false;
    passportEndPageNumber.value = totalResultPages;
  } else {
    passportIsRangeGreater.value = true;
  }

  passportIsRangeLess.value = passportStartPageNumber.value >= LabelsPerPage;
};

const displaySiaResultPageList = (currentPageNo, totalResultPages: number = 4): void => {
  const sectionNo = Math.floor(currentPageNo / LabelsPerPage);
  siaStartPageNumber.value = sectionNo * LabelsPerPage;
  siaEndPageNumber.value = (sectionNo + 1) * LabelsPerPage;

  if (siaEndPageNumber.value >= totalResultPages) {
    siaIsRangeGreater.value = false;
    siaEndPageNumber.value = totalResultPages;
  } else {
    siaIsRangeGreater.value = true;
  }

  siaIsRangeLess.value = siaStartPageNumber.value >= LabelsPerPage;
};

const scanExpiryDates = (): void => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.token}`,
      'Content-Type': 'application/json'
    },
  };
  
  const url = (props.baseUrl ?? smsapi_endpoint_base_url) + "Securityofficers/ScanExiryDates";
  fetch(url, options)
    .then((response: Response) => {
      if (response.status === 401) {
        isAuthorized.value = false;
        return null;
      }
      isAuthorized.value = true;
      return response.json();
    })
    .catch((error: Error) => {
      console.error("Error in scanExpiryDates:", error);
    });
};

const getAuthorization = (): void => {
  const token = localStorage.getItem("token");
  isAuthorized.value = token !== null && token !== "";
};

// Lifecycle hooks
onMounted(() => {
  getAuthorization();
});

watch(
  () => props.securityOfficerId,
  (newVal: string | undefined) => {
    console.log("selectedOfficerIdForModal changed to:", newVal);
  }
);

__expose({
  scanExpiryDates,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("input", {
        type: "text",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchString).value = $event)),
        placeholder: "Enter Search String"
      }, null, 512), [
        [_vModelText, searchString.value]
      ]),
      _createElementVNode("button", {
        type: "button",
        class: "btn btn-primary btn-sm m-2",
        onClick: scanExpiryDates
      }, "ScanExpiryDates"),
      _createElementVNode("button", {
        type: "button",
        class: "btn btn-primary btn-sm m-2",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (visaExpiryDatesDisplayNew(0, true)))
      }, "DisplayExpiryDates")
    ]),
    _createElementVNode("div", null, [
      _withDirectives(_createElementVNode("span", _hoisted_3, "Please make sure you are logged in.", 512), [
        [_vShow, !isAuthorized.value]
      ])
    ]),
    _createVNode(ExpiryTableSection, {
      data: visaExpiryList.value,
      total: visaExpiryCount.value,
      title: "VISA EXPIRY LIST",
      "current-page": currentVisaExpiryPageNo.value,
      "start-page": visaStartPageNumber.value,
      "end-page": visaEndPageNumber.value,
      "is-range-greater": visaIsRangeGreater.value,
      "is-range-less": visaIsRangeLess.value,
      "labels-per-page": LabelsPerPage,
      "expired-count": visaExpiredCount.value,
      "immediately-expiring-count": visaImmediatelyExpiringCount.value,
      "soon-expiring-count": visaSoonExpiringCount.value,
      "valid-count": visaValidCount.value,
      onPrevPage: _cache[2] || (_cache[2] = ($event: any) => (visaExpiryDatesDisplayNew(visaStartPageNumber.value - LabelsPerPage))),
      onNextPage: _cache[3] || (_cache[3] = ($event: any) => (visaExpiryDatesDisplayNew(visaEndPageNumber.value))),
      onPageChange: _cache[4] || (_cache[4] = ($event: any) => (visaExpiryDatesDisplayNew($event))),
      onFilterChange: _cache[5] || (_cache[5] = (filters) => handleFilterChange('Visa', filters))
    }, null, 8, ["data", "total", "current-page", "start-page", "end-page", "is-range-greater", "is-range-less", "expired-count", "immediately-expiring-count", "soon-expiring-count", "valid-count"]),
    _createVNode(ExpiryTableSection, {
      data: passportExpiryList.value,
      total: passportExpiryCount.value,
      title: "PASSPORT EXPIRY LIST",
      "current-page": currentPassportExpiryPageNo.value,
      "start-page": passportStartPageNumber.value,
      "end-page": passportEndPageNumber.value,
      "is-range-greater": passportIsRangeGreater.value,
      "is-range-less": passportIsRangeLess.value,
      "labels-per-page": LabelsPerPage,
      "expired-count": passportExpiredCount.value,
      "immediately-expiring-count": passportImmediatelyExpiringCount.value,
      "soon-expiring-count": passportSoonExpiringCount.value,
      "valid-count": passportValidCount.value,
      onPrevPage: _cache[6] || (_cache[6] = ($event: any) => (passportExpiryDatesDisplayNew(passportStartPageNumber.value - LabelsPerPage))),
      onNextPage: _cache[7] || (_cache[7] = ($event: any) => (passportExpiryDatesDisplayNew(passportEndPageNumber.value))),
      onPageChange: _cache[8] || (_cache[8] = ($event: any) => (passportExpiryDatesDisplayNew($event))),
      onFilterChange: _cache[9] || (_cache[9] = (filters) => handleFilterChange('Passport', filters))
    }, null, 8, ["data", "total", "current-page", "start-page", "end-page", "is-range-greater", "is-range-less", "expired-count", "immediately-expiring-count", "soon-expiring-count", "valid-count"]),
    _createVNode(ExpiryTableSection, {
      data: siaBadgeExpiryList.value,
      total: siaBadgeExpiryCount.value,
      title: "SIA BADGE EXPIRY LIST",
      "current-page": currentSiaBadgeExpiryPageNo.value,
      "start-page": siaStartPageNumber.value,
      "end-page": siaEndPageNumber.value,
      "is-range-greater": siaIsRangeGreater.value,
      "is-range-less": siaIsRangeLess.value,
      "labels-per-page": LabelsPerPage,
      "expired-count": siaExpiredCount.value,
      "immediately-expiring-count": siaImmediatelyExpiringCount.value,
      "soon-expiring-count": siaSoonExpiringCount.value,
      "valid-count": siaValidCount.value,
      onPrevPage: _cache[10] || (_cache[10] = ($event: any) => (siaBadgeExpiryDatesDisplayNew(siaStartPageNumber.value - LabelsPerPage))),
      onNextPage: _cache[11] || (_cache[11] = ($event: any) => (siaBadgeExpiryDatesDisplayNew(siaEndPageNumber.value))),
      onPageChange: _cache[12] || (_cache[12] = ($event: any) => (siaBadgeExpiryDatesDisplayNew($event))),
      onFilterChange: _cache[13] || (_cache[13] = (filters) => handleFilterChange('SIA_Badge', filters))
    }, null, 8, ["data", "total", "current-page", "start-page", "end-page", "is-range-greater", "is-range-less", "expired-count", "immediately-expiring-count", "soon-expiring-count", "valid-count"])
  ]))
}
}

})